import PropTypes from 'prop-types';
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useIntl } from 'react-intl';

/**
 * CustomToolbar component for the DataGrid, providing buttons for adding, deleting,
 * and duplicating rows, as well as standard toolbar functionalities like column selection,
 * filtering, and density selection.
 *
 * @component
 *
 * @example
 * <KnowledgeBaseCustomToolbar onAdd={handleAdd} onDelete={handleDelete} onDuplicate={handleDuplicate} selectedRows={selectedRows} />
 *
 * @param {Object} props - The properties object.
 * @param {Function} props.onDelete - Function to call when the delete button is clicked.
 * @param {Array} props.selectedRows - Array of selected rows.
 *
 * @returns {React.Component} The Custom Toolbar component.
 */
function KnowledgeBaseCustomToolbar({ onDelete, selectedRows }) {
  const { formatMessage: msg } = useIntl();

  const oneSelected = selectedRows.length === 1;
  const manySelected = selectedRows.length > 1;

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Box sx={{ flexGrow: 1 }} />
      <Tooltip title={msg({ id: `action.delete` })}>
        <span>
          <IconButton
            type="button"
            aria-label="delete"
            color="error"
            sx={{ mr: -1 }}
            size="large"
            onClick={onDelete}
            disabled={!oneSelected && !manySelected}
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
    </GridToolbarContainer>
  );
}

KnowledgeBaseCustomToolbar.propTypes = {
  onDelete: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired
};

export default KnowledgeBaseCustomToolbar;
